import { ToastProvider } from "components/atoms/toast/toast-context";
import { Layout } from "components/layout/layout";
import { Loader } from "components/loader/loader";
import AuthGuard from "hoc/AuthGuard";
import { useLoading } from "hooks/pages/use-loading/use-loading";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";

import { SessionProvider } from "next-auth/react";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import {
  EventPayload,
  registerSuperProperties
} from "services/mixpanel/mixpanel";
import { AuthProvider } from "stores/auth/auth-context";
import { KelasLiveContextProvider } from "stores/kelas-live/kelas-live-context";
import { TanyaProvider } from "stores/tanya/tanya-context";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import { GlobalStyle, theme } from "styles";
import { AppProps } from "types/next-auth";
import { GrowthBookContextProvider } from "../stores/growthbook/growthbook-context";

const mixPanelToken = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
const mixPanelDebug = process.env.NEXT_ENV != "production" ? true : false;

export const loadMixpanel = async () => {
  const mixpanel = (await import("mixpanel-browser")).default;
  mixPanelToken && mixpanel.init(mixPanelToken, { debug: mixPanelDebug });
};

export const batchEvents: Array<{ eventName: string; payload?: EventPayload }> =
  [];

const CoLearnApp: React.FC<AppProps> = ({ Component, pageProps }) => {
  const router = useRouter();
  const currentPlatform = useGetCurrentPlatform();

  const { isLoading, loadingPageName } = useLoading();

  useEffect(() => {
    loadMixpanel().then(() => {
      registerSuperProperties({
        platform: currentPlatform
      });
    });
  }, []);

  const isTanyaVideoPage = router.pathname === "/tanya/[questionId]/[slug]";

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Meta Pixel Code */}
        <script
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '362736946099784');
            fbq('track', 'PageView');`
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              document.addEventListener("DOMContentLoaded", function () {
                document.body.addEventListener("click", function (event) {
                  if (event.target.closest("#oke-minta-info-bimbel-button")) {
                    fbq("track", "Lead");
                  }
                });
              });
            `
          }}
        />
        <noscript>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=362736946099784&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        {/* End Meta Pixel Code */}
      </Head>

      {!isTanyaVideoPage && (
        <StyleSheetManager shouldForwardProp={() => true}>
          <AuthProvider>
            <SessionProvider session={pageProps.session}>
              <ThemeProvider theme={theme}>
                <KelasLiveContextProvider>
                  <GrowthBookContextProvider>
                    <TanyaProvider>
                      <GlobalStyle theme={theme} />
                      <ToastProvider>
                        {Component.requireAuth ? (
                          <AuthGuard>
                            <Layout pageProps={pageProps}>
                              <Component {...pageProps} />
                            </Layout>
                          </AuthGuard>
                        ) : (
                          <Layout pageProps={pageProps}>
                            {isLoading ? (
                              <Loader loadingPageName={loadingPageName} />
                            ) : (
                              <Component {...pageProps} />
                            )}
                          </Layout>
                        )}
                      </ToastProvider>
                    </TanyaProvider>
                  </GrowthBookContextProvider>
                </KelasLiveContextProvider>
              </ThemeProvider>
            </SessionProvider>
          </AuthProvider>
        </StyleSheetManager>
      )}
      {isTanyaVideoPage && (
        <ThemeProvider theme={theme}>
          <TanyaProvider>
            <GlobalStyle theme={theme} />
            <ToastProvider>
              <Layout pageProps={pageProps}>
                {isLoading ? (
                  <Loader loadingPageName={loadingPageName} />
                ) : (
                  <Component {...pageProps} />
                )}
              </Layout>
            </ToastProvider>
          </TanyaProvider>
        </ThemeProvider>
      )}
    </>
  );
};

export default CoLearnApp;
